import { z } from "zod";

const bounded = (max: number) => z.string().trim().min(1).max(max);
export const aiFeedbackReasonSchema = z.enum(["incorrect_conclusion", "missing_context", "wrong_owner", "wrong_priority", "not_actionable", "other"]);
export const aiFeedbackRequestSchema = z.object({
  rating: z.enum(["up", "down"]),
  reason: aiFeedbackReasonSchema.nullable().optional(),
  correction: bounded(2_000).nullable().optional(),
  comment: bounded(1_000).nullable().optional(),
}).strict().superRefine((value, context) => {
  if (value.rating === "down" && !value.reason) context.addIssue({ code: z.ZodIssueCode.custom, path: ["reason"], message: "Choose a reason for negative feedback." });
  if (value.rating === "up" && value.reason) context.addIssue({ code: z.ZodIssueCode.custom, path: ["reason"], message: "Positive feedback cannot include a negative reason." });
});
export const aiFeedbackSchema = z.object({
  responseId: z.string().uuid(), rating: z.enum(["up", "down"]), reason: aiFeedbackReasonSchema.nullable(),
  correction: z.string().nullable(), comment: z.string().nullable(), updatedAt: z.string().datetime(),
});
export const aiFeedbackResponseSchema = z.object({ feedback: aiFeedbackSchema });
export const aiFeedbackLookupResponseSchema = z.object({ feedback: aiFeedbackSchema.nullable() });
export const aiOutcomeRequestSchema = z.object({ outcome: z.enum(["accepted", "edited", "rejected", "succeeded", "failed"]), note: bounded(1_000).nullable().optional() }).strict();
export const aiOutcomeResponseSchema = z.object({ recorded: z.literal(true) });
export const aiEvaluationSummarySchema = z.object({
  workflow: bounded(100), recommendations: z.number().int().nonnegative(), abstentions: z.number().int().nonnegative(), rated: z.number().int().nonnegative(),
  positive: z.number().int().nonnegative(), negative: z.number().int().nonnegative(),
  positiveRate: z.number().min(0).max(1).nullable(), corrections: z.number().int().nonnegative(),
  outcomes: z.record(z.number().int().nonnegative()), reasons: z.record(z.number().int().nonnegative()),
  configurations: z.array(z.object({
    key: bounded(500), pipelineVersion: bounded(100), provider: bounded(100), modelVersion: bounded(200), promptVersion: bounded(200),
    schemaVersion: bounded(200), retrievalVersion: bounded(200), recommendations: z.number().int().nonnegative(),
    abstentions: z.number().int().nonnegative(),
    rated: z.number().int().nonnegative(), positiveRate: z.number().min(0).max(1).nullable(),
  })),
});
export type AiEvaluationSummary = z.infer<typeof aiEvaluationSummarySchema>;
export type AiFeedbackRequest = z.infer<typeof aiFeedbackRequestSchema>;

const evaluationObservationSchema = z.object({
  correct: z.boolean(), confidence: z.number().min(0).max(1), abstained: z.boolean(),
  latencyMs: z.number().nonnegative(), costUsd: z.number().nonnegative(),
}).strict();
export const aiEvaluationComparisonRequestSchema = z.object({
  workflow: bounded(100), datasetVersion: bounded(100), baselinePipelineVersion: bounded(100),
  candidatePipelineVersion: bounded(100), cases: z.array(z.object({
    caseId: bounded(200), segment: bounded(100), baseline: evaluationObservationSchema, candidate: evaluationObservationSchema,
  }).strict()).min(1).max(500),
}).strict();
const evaluationMetricsSchema = z.object({
  cases: z.number().int().positive(), accuracy: z.number().min(0).max(1), calibrationError: z.number().min(0).max(1),
  abstentionRate: z.number().min(0).max(1), averageLatencyMs: z.number().nonnegative(), totalCostUsd: z.number().nonnegative(),
});
export const aiEvaluationReportSchema = z.object({
  id: z.string().uuid(), workflow: bounded(100), datasetVersion: bounded(100), baselinePipelineVersion: bounded(100),
  candidatePipelineVersion: bounded(100), baseline: evaluationMetricsSchema, candidate: evaluationMetricsSchema,
  recommendation: z.enum(["candidate_better", "baseline_better", "insufficient_evidence"]),
  segmentRegressions: z.array(bounded(100)), decision: z.enum(["pending", "approved", "rejected"]),
  reviewerId: z.string().nullable(), reviewedAt: z.string().datetime().nullable(), createdAt: z.string().datetime(),
});
export const aiEvaluationReportResponseSchema = z.object({ report: aiEvaluationReportSchema });
export const aiEvaluationReportLookupSchema = z.object({ report: aiEvaluationReportSchema.nullable() });
export const aiEvaluationDecisionRequestSchema = z.object({ decision: z.enum(["approved", "rejected"]) }).strict();
export type AiEvaluationComparisonRequest = z.infer<typeof aiEvaluationComparisonRequestSchema>;
export type AiEvaluationReport = z.infer<typeof aiEvaluationReportSchema>;
